import { cn } from '@/lib/utils'
import { Search, XIcon } from 'lucide-react'
import { type RefObject, useRef } from 'react'
import { Button } from '../ui/button'
import { Input, type InputProps } from '../ui/input'

export type SearchInputProps = Omit<InputProps, 'value'> & {
  value?: string
  setValue: (value: string) => void
  onClear?: () => void
  inputRef?: RefObject<HTMLInputElement | null>
}

const SearchInput = ({
  value,
  setValue,
  onClear,
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
  onClick,
  disabled = false,
  autoComplete,
  inputRef,
  className = '',
  placeholder,
  ...inputProps
}: SearchInputProps) => {
  const fallbackInputRef = useRef<HTMLInputElement>(null)
  const resolvedInputRef = inputRef ?? fallbackInputRef
  onChange ??= (e) => setValue(e.target.value)
  onClear ??= () => {
    setValue('')
  }
  const handleClear = () => {
    onClear()
    resolvedInputRef.current?.focus()
  }

  return (
    <div className={cn('relative', className)}>
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50" />
      <Input
        {...inputProps}
        ref={resolvedInputRef}
        className="pl-9 pr-9"
        type="text"
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        autoComplete={autoComplete}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        onClick={onClick}
      />
      {!!value && (
        <Button
          variant="ghostMuted"
          size="icon-2xs"
          aria-label="Clear search"
          disabled={disabled}
          onMouseDown={(event) => event.preventDefault()}
          onClick={handleClear}
          className="absolute right-2 top-1/2 -translate-y-1/2"
        >
          <XIcon className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}

export default SearchInput
